/**
 * 路由表：/{locale}/... 双层结构。
 * M2：notices/builders/contact×3 全部接入定稿页面；about 升级为原型布局。
 */
import { createBrowserRouter, Outlet, useLocation } from 'react-router';
import { defaultLocale, isLocale, locales, localeFromPathname } from './locales';
import { ErrorBoundaryPage } from './ErrorBoundaryPage';
import { TownPage } from '@/pages/TownPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LanguageRedirect } from './LanguageRedirect';
import { AboutPage } from '@/pages/AboutPage';
import { NoticesPage } from '@/pages/NoticesPage';
import { BuildersPage } from '@/pages/BuildersPage';
import { ContactPage } from '@/pages/ContactPage';
import { MessengerPage } from '@/pages/MessengerPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { LocaleProvider } from '@/lib/locale';
import { ContentThemeProvider } from '@/lib/content-theme';

/** LocaleProvider 包裹的布局（locale 从路径提取）。
 * 注意：localeRoutes 生成的是每语言静态路径（/zh-CN、/en），无 :locale param——
 * useParams() 恒空导致 /en 一直回落 zh-CN（M1 起潜伏，M3 首页双语走查时暴露）。
 * 改为从 pathname 首段解析。 */
function LocaleLayout() {
  const { pathname } = useLocation();
  const current = localeFromPathname(pathname) ?? defaultLocale;
  return (
    <LocaleProvider locale={current}>
      <ContentThemeProvider>
        <Outlet />
      </ContentThemeProvider>
    </LocaleProvider>
  );
}

/** 对已知页面路径生成各语言的静态路由 */
function localeRoutes(locale: string) {
  const base = `/${locale}`;
  return {
    path: base,
    element: <LocaleLayout />,
    errorElement: <ErrorBoundaryPage />,
    children: [
      { index: true, element: <TownPage /> },
      { path: 'builders', element: <BuildersPage /> },
      { path: 'notices', element: <NoticesPage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'contact/creators', element: <MessengerPage scene='creators' /> },
      { path: 'contact/partners', element: <MessengerPage scene='partners' /> },
      { path: 'privacy', element: <PrivacyPage /> }, // M4 落页（决策 4：海外标准；文案待品牌/法务终审）
      { path: '*', element: <NotFoundPage /> },
    ],
  };
}

export const router = createBrowserRouter([
  // 顶层通配：任何非 locale 前缀路径由 LanguageRedirect 重定向到偏好语言
  {
    path: '/',
    element: <LanguageRedirect />,
    errorElement: <ErrorBoundaryPage />,
    children: [{ path: '404', element: <NotFoundPage /> }],
  },
  ...locales.map(localeRoutes),
  { path: '*', element: <NotFoundPage /> },
]);

export { defaultLocale, isLocale };
